import { captureException } from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import i18n from "i18next";
import { toast } from "sonner";
import {
	NsureClientTypeEnum,
	type NsureQuestionnaireAccessRequestDTO,
	type NsureQuestionnaireAccessResponseDTO,
	postNsureQuestionnaireAccessV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import { handleMyPlannResponseError } from "web_myplann/Infrastructure/handleMyPlannResponseError";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { isEnumValue } from "web_myplann/Shared/Utils/parsers";
import { device, withProcessingOverlay } from "web_myplann/Utils/device";

export type QuestionnaireNotificationPayload = {
	notificationId?: number;
	clientType: unknown;
	identificationNumber: unknown;
};

export const getQuestionnaireAccessRequest = ({
	notificationId,
	clientType,
	identificationNumber,
}: QuestionnaireNotificationPayload):
	| NsureQuestionnaireAccessRequestDTO
	| undefined => {
	if (
		isEnumValue(NsureClientTypeEnum, clientType) &&
		typeof identificationNumber === "string" &&
		identificationNumber
	) {
		return { identificationNumber, clientType };
	}

	captureException(
		new Error(
			`Investment questionnaire notification ${notificationId} carries an unusable payload (clientType: ${String(clientType)})`,
		),
	);
	toast.error(i18n.t(Resources.Common.somethingWentWrong));

	return undefined;
};

export const openQuestionnaire = async (
	access: NsureQuestionnaireAccessResponseDTO,
): Promise<boolean> => {
	try {
		const opened = await device.openExternalUrl(access.url, [
			{
				name: "clientZoneExtToken",
				value: access.accessToken,
				domain: new URL(access.url).hostname,
				path: "/",
				secure: true,
			},
		]);

		return opened.payload.success;
	} catch (error) {
		captureException(error);

		return false;
	}
};

export const openQuestionnaireFromPush = async (
	payload: QuestionnaireNotificationPayload,
	queryClient: QueryClient,
): Promise<boolean> => {
	const requestContract = getQuestionnaireAccessRequest(payload);

	if (!requestContract) {
		return false;
	}

	await withProcessingOverlay(async () => {
		try {
			const access = await processResponse(postNsureQuestionnaireAccessV2)(
				requestContract,
			);

			if (!(await openQuestionnaire(access))) {
				toast.error(i18n.t(Resources.Common.somethingWentWrong));
			}
		} catch (error) {
			handleMyPlannResponseError(error, queryClient);
		}
	});

	return true;
};
